"use client";
import { Input } from "@/components/ui/Input";
import { useSubscribe } from "@frend-digital/centra/client";

import { NewsletterCheckbox } from "@/features/account/NewsletterCheckbox";
import { useTranslations } from "@/i18n/client";
import { Button } from "@/components/ui/Button";
import type { StoryblokRichtext } from "@/storyblok-base";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import styles from "../../Footer.module.css";

const useSuccessMessage = (timeout?: number) => {
	const [successMessage, setSuccessMessage] = useState<string>("");

	useEffect(() => {
		if (successMessage && timeout) {
			const timer = setTimeout(() => {
				setSuccessMessage("");
			}, timeout);

			return () => clearTimeout(timer);
		}
	}, [successMessage, timeout]);

	return { successMessage, setSuccessMessage };
};

export const Newsletter = ({
	title,
	text,
	placeholder,
	id,
	signupTerms,
}: {
	title?: string;
	text?: string;
	placeholder?: string;
	id?: string;
	signupTerms?: StoryblokRichtext;
}) => {
	const t = useTranslations();
	const newsletterSignupSchema = z
		.object({
			email: z.string().email({ message: t("Please enter your email address") }),
			newsletter: z.boolean(),
		})
		.superRefine((data, ctx) => {
			if (data.email && !data.newsletter) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("You must accept the terms to subscribe"),
					path: ["newsletter"],
				});
			}
		});

	type NewsletterInput = z.infer<typeof newsletterSignupSchema>;

	const subscribe = useSubscribe();

	const { successMessage, setSuccessMessage } = useSuccessMessage(5000);

	const methods = useForm<NewsletterInput>({
		resolver: zodResolver(newsletterSignupSchema),
		defaultValues: {
			email: "",
			newsletter: false,
		},
	});

	const {
		register,
		handleSubmit,
		setError,
		reset,
		formState: { errors },
	} = methods;

	const onSubmit: SubmitHandler<NewsletterInput> = async ({ email }) => {
		subscribe.mutate(
			{
				email,
			},
			{
				onSuccess: () => {
					setSuccessMessage(
						t("Thank you for subscribing! Check your inbox for a confirmation email."),
					);
					reset();
				},
				onError: () => {
					setError("email", {
						message: t("Something went wrong"),
					});
				},
			},
		);
	};

	return (
		<FormProvider {...methods}>
			<form className={styles.menu} onSubmit={handleSubmit(onSubmit)}>
				<h2 className={styles.title}>{title}</h2>
				<div className={styles.container}>
					<p>{text}</p>
				</div>
				<div className={styles.newsletterWrapper}>
					<label htmlFor={id} className="sr-only">
						{t("Email")}
					</label>
					<Input
						id={id}
						placeholder={placeholder}
						className={styles.input}
						{...register("email", { required: true })}
					/>
					<Button type="submit" loading={subscribe.isPending} className={styles.newsletterButton}>
						{t("Subscribe")}
					</Button>
					{errors.email && <p className={styles.error}>{errors.email?.message}</p>}
					<div className={styles.newsletterCheckbox}>
						<NewsletterCheckbox signupTerms={signupTerms} signupLabel="" showError={false} />
					</div>
					{errors.newsletter && <p className={styles.error}>{errors.newsletter?.message}</p>}
				</div>
				{successMessage && <p className={styles.success}>{successMessage}</p>}
			</form>
		</FormProvider>
	);
};
